import React from "react";
import { motion } from "framer-motion";
const logoBase = "/assets/dental-software-logos";
const additionalLogos = "/assets/additional-dental-logos";

const vendorCategories = [
  {
    category: "Practice Management",
    cost: "$400–700/mo",
    logos: [
      { src: `${logoBase}/pms/dentrix.png`, name: "Dentrix" },
      { src: `${logoBase}/pms/eaglesoft.png`, name: "Eaglesoft" },
      { src: `${logoBase}/pms/opendental.png`, name: "OpenDental" },
    ],
    color: "border-blue-200 bg-blue-50/50",
    accent: "text-blue-700",
    dot: "bg-blue-500",
  },
  {
    category: "Communications",
    cost: "$300–600/mo",
    logos: [
      { src: `${logoBase}/comms/weave.png`, name: "Weave" },
      { src: `${logoBase}/comms/mango.png`, name: "Mango" },
      { src: `${additionalLogos}/communications/podium.png`, name: "Podium" },
    ],
    color: "border-purple-200 bg-purple-50/50",
    accent: "text-purple-700",
    dot: "bg-purple-500",
  },
  {
    category: "Payments",
    cost: "$100–300/mo + fees",
    logos: [
      { src: `${logoBase}/payments/global_payments.png`, name: "Global Payments" },
      { src: `${additionalLogos}/payments/square.png`, name: "Square" },
    ],
    color: "border-emerald-200 bg-emerald-50/50",
    accent: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  {
    category: "Insurance Verification",
    cost: "$200–400/mo",
    logos: [
      { src: `${logoBase}/insurance/zuub.png`, name: "Zuub" },
      { src: `${logoBase}/insurance/airpay.png`, name: "AirPay" },
    ],
    color: "border-indigo-200 bg-indigo-50/50",
    accent: "text-indigo-700",
    dot: "bg-indigo-500",
  },
  {
    category: "Revenue Intelligence",
    cost: "$300–500/mo",
    logos: [
      { src: `${logoBase}/revenue/dental_intel.png`, name: "Dental Intel" },
    ],
    color: "border-amber-200 bg-amber-50/50",
    accent: "text-amber-700",
    dot: "bg-amber-500",
  },
  {
    category: "Claims & eClaims",
    cost: "$150–300/mo",
    logos: [
      { src: `${logoBase}/claims/vyne_trellis.png`, name: "Vyne Trellis" },
      { src: `${logoBase}/claims/dentalxchange.png`, name: "DentalXChange" },
    ],
    color: "border-red-200 bg-red-50/50",
    accent: "text-red-700",
    dot: "bg-red-500",
  },
  {
    category: "AI Receptionist",
    cost: "$300–500/mo",
    logos: [
      { src: `${logoBase}/ai_receptionist/dentina.jpg`, name: "Dentina" },
      { src: `${additionalLogos}/ai_receptionist/arini.png`, name: "Arini" },
    ],
    color: "border-rose-200 bg-rose-50/50",
    accent: "text-rose-700",
    dot: "bg-rose-500",
  },
  {
    category: "Marketing",
    cost: "$300–500/mo",
    logos: [
      { src: `${logoBase}/marketing/revenuewell.jpg`, name: "RevenueWell" },
      { src: `${additionalLogos}/marketing/practicemojo.png`, name: "PracticeMojo" },
    ],
    color: "border-pink-200 bg-pink-50/50",
    accent: "text-pink-700",
    dot: "bg-pink-500",
  },
  {
    category: "AI Case Acceptance",
    cost: "$200–400/mo",
    logos: [
      { src: `${logoBase}/ai_case_acceptance/codent.png`, name: "Codent" },
      { src: `${logoBase}/ai_case_acceptance/avora.jpg`, name: "Avora" },
    ],
    color: "border-cyan-200 bg-cyan-50/50",
    accent: "text-cyan-700",
    dot: "bg-cyan-500",
  },
  {
    category: "Imaging Software",
    cost: "$200–400/mo",
    logos: [
      { src: `${additionalLogos}/imaging/dexis.png`, name: "Dexis" },
      { src: `${logoBase}/imaging/apteryx.png`, name: "Apteryx" },
      { src: `${logoBase}/imaging/carestream.jpg`, name: "Carestream" },
    ],
    color: "border-violet-200 bg-violet-50/50",
    accent: "text-violet-700",
    dot: "bg-violet-500",
  },
];

const ConsolidationSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col items-center justify-center p-3 xl:p-5 font-sans relative overflow-hidden">
      {/* Background blurs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-100/30 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-orange-100/30 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />

      <div className="max-w-[1250px] w-full h-full flex flex-col justify-center relative z-10">
        {/* Header */}
        <div className="text-center mb-3 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]">
              But you're never going to get to that threshold when your staff has to work across{" "}
              <span className="text-blue-600">10 different softwares</span>...
            </h2>
          </motion.div>
        </div>

        {/* Vendor grid */}
        <div className="flex flex-col">
          <div className="grid grid-cols-5 gap-2.5">
            {vendorCategories.map((v, i) => (
              <motion.div
                key={v.category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05, duration: 0.4 }}
                className={`rounded-xl border ${v.color} p-2.5 flex flex-col shadow-sm`}
              >
                {/* Category name */}
                <div className="flex items-center gap-1.5 mb-2 flex-shrink-0">
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${v.dot} flex-shrink-0`}
                  />
                  <h3
                    className={`text-[10px] font-bold ${v.accent} leading-tight uppercase tracking-wide`}
                  >
                    {v.category}
                  </h3>
                </div>

                {/* Logos */}
                <div className="flex flex-col gap-1.5 flex-1 justify-center">
                  {v.logos.map((logo) => (
                    <div
                      key={logo.name}
                      className="flex items-center gap-2 bg-white rounded-lg px-2.5 py-1.5 border border-slate-100 shadow-sm"
                    >
                      <img
                        src={logo.src}
                        alt={logo.name}
                        className="h-5 w-auto max-w-[60px] object-contain flex-shrink-0"
                      />
                      <span className="text-[11px] font-semibold text-slate-700 truncate">
                        {logo.name}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsolidationSlide;
